import { NgApiClient } from '@src/api/ngApiClient';
import { IPredicate } from '@src/models/api';
import { getPath } from '@src/utils/getPath';
import { TFilterScheme, TFilterSchemeOptions, TFilterValues, TControllerValue } from '../models';
import { mapToDefaultFilter } from '../utils';
import { fetchOptions } from './api';
import { getFilterValue, getPredicateFromValue, getValueFromPredicate } from './utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { AnyType } from '@src/models/common';

type TCacheValue = { isLoaded?: boolean };
type TSchemeState = Map<string, TFilterScheme>;
type TValuesState = Map<string, TControllerValue>;

export class FiltersController {
  /** Схема фильтров (базовые настройки) */
  private scheme: TSchemeState = new Map();

  /** Значение фильтров */
  private valueList: TValuesState = new Map();

  /** Хранилище для информации о действиях с кешем, string - имя события */
  private cacheMap = new Map<string, TCacheValue>();

  /** Используем для запросов к апи, допустим при подгрузке опций */
  private ngApiClient: NgApiClient;

  /** Subject - потому, что он предоставляет больший контроль над данными, но они обязательно должны быть приватны */
  private schemeSubject: BehaviorSubject<TSchemeState>;
  private valueListSubject: BehaviorSubject<TValuesState>;
  /** @deprecated для возможности подписки на обновление дефолтных значенией. Надо будет переделать модель данных у `value` и его выпилить */
  public updateDefaultValuesSubject = new BehaviorSubject(this.valueList);

  /** Предоставим доступ для подписки снаружи */
  public scheme$: Observable<TSchemeState>;
  public valueList$: Observable<TValuesState>;

  constructor({
    scheme,
    predicates,
    ngApiClient,
  }: {
    ngApiClient: NgApiClient;
    scheme: TFilterScheme[];
    predicates?: IPredicate[];
  }) {
    this.ngApiClient = ngApiClient;

    /** Инициилизируем схему */
    for (let index = 0; index < scheme.length; index++) {
      const filter = scheme[index];

      this.scheme.set(filter.name, mapToDefaultFilter(filter));
    }

    this.schemeSubject = new BehaviorSubject(this.scheme);
    this.scheme$ = this.schemeSubject.asObservable();

    /** Сделаем проверку того, что в схеме есть указанные фильтры */
    const newValueList = predicates ? this.getValuesFromPredicates(predicates) : new Map();
    this.valueList = newValueList;
    this.valueListSubject = new BehaviorSubject(this.valueList);
    this.valueList$ = this.valueListSubject.asObservable();
  }

  private getValuesFromPredicates(predicates: IPredicate[], resetValue?: boolean) {
    const values = getValueFromPredicate(predicates, this.scheme);
    let newValueList: TValuesState = new Map();

    if (resetValue) {
      Array.from(values).forEach(([key, defaultValue]) => {
        defaultValue && newValueList.set(key, { defaultValue, value: defaultValue });
      });
    } else {
      newValueList = new Map(this.valueList);
      Array.from(values).forEach(([key, value]) => {
        value && newValueList.set(key, { value, defaultValue: value });
      });
    }

    return newValueList;
  }

  /**
   * Метод для обновления дефолтных `(примененных)` значений фильтров
   *
   * Метод очень тяжелый и обновляет почти все компоненты
   *
   * @param resetValue обнулять ли накликанные значение, по умолчанию `true`
   *  */
  public updateDefaultValues = (predicates: IPredicate[] = [], resetValue = true) => {
    const newValueList = this.getValuesFromPredicates(predicates, resetValue);

    this.valueList = newValueList;
    this.valueListSubject.next(this.valueList);
    this.updateDefaultValuesSubject.next(this.valueList);
  };

  private setCache(params: [string, TCacheValue]) {
    this.cacheMap.set(...params);
  }

  private getCache(name: string) {
    return this.cacheMap.get(name);
  }

  /** Изменение определенного фильтра
   * @param name имя фильтра
   * @param value его новое значение
   */
  public onChange = (name: string, value: TFilterValues) => {
    const filter = this.scheme.get(name);
    if (!filter) return;

    const filterValue = { ...getFilterValue(this.valueList, name) };
    filterValue.value = value;
    this.valueList.set(filter.name, filterValue);
    this.valueList = new Map(this.valueList);

    this.valueListSubject.next(this.valueList);
  };

  /** Изменить схему фильтра */
  public mutateFilterScheme = (name: string, f: Partial<TFilterScheme>) => {
    const filter = this.scheme.get(name);
    if (!filter) return;

    this.scheme.set(filter.name, { ...filter, ...f } as TFilterScheme);
    this.scheme = new Map(this.scheme);
    this.schemeSubject.next(this.scheme);
  };

  /** Очистить все фильтры, по умолчанию очищает только `накликанные` значение, `дефолтные` оставляет
   * @param force очистить полностью все значения, и дефолтные и накликанные
   */
  public onResetList = (force?: boolean) => {
    if (force) {
      this.valueList.clear();
    } else {
      this.valueList.forEach((v, key) => {
        const value = v;
        if (value?.value) {
          value.value = null;
        }
        this.valueList.set(key, value);
      });
    }

    this.valueList = new Map(this.valueList);
    this.valueListSubject.next(this.valueList);
  };

  /** Текущие предикаты фильтров (дефолтных и накликанных) */
  predicate(valueList = this.valueList): IPredicate[] {
    return getPredicateFromValue(valueList, this.scheme);
  }

  /** Текущий `url` фильтров (дефолтных и накликанных) */
  get url(): string {
    return getPath({ selector: { predicates: this.predicate() } });
  }

  /** Кол-во дефолтных фильтров, именно они считаются как примененные */
  get count(): number {
    let count = 0;

    Array.from(this.valueList).forEach(([, v]) => v?.defaultValue && count++);

    return count;
  }

  /**
   * Функция для загрузки опций фильтра
   * TODO: здесь особо ей не место, но почему бы и нет
   * 
   * параметр `data` - это любая производная информация, 
   * которая будет передоваться в вызов функции `filter.getOptions` 
   * и может использоваться для более детального запроса получения списка опций
   * 
   * на примере ниже мы передаем clientId для того чтобы исключить текущего клиента из списка опций
   * @example
   * 
   *    const CLIENT_FILTER: TFilter = {
        name: OPERATION_FILTER_NAME.CLIENT,
        type: 'options',
        source: 'external',
        getOptions: async ({ ngApiClient, data }) => {
          try {
            const clientId = data.clientId
            const res = await ngApiClient.get<IBalanceOperation, string>('balanceOperation', {
              selector: {
                fields: ['clientId', 'clientName'],
                predicates: [{field: 'clientId', operator: 'NOT_EQUALS', values: [clientId]}]
              },
            });

            const clientMap = new Map<number, Pick<IBalanceOperation, 'clientId' | 'clientName'>>();
            res.data.page.entries.forEach((c) => !!c && clientMap.set(c.clientId!, { ...c }));
            return Array.from(clientMap).map(([, c]) => ({ label: c.clientName, value: c.clientId, id: c.clientId }));
          } catch (error) {
            console.error('Error getching clientId for balance operation', error);
            return [];
          }
        },
        title: 'Клиент',
        filterField: 'label',
      }; 
   */
  public loadOption = async (filter: TFilterSchemeOptions, data?: AnyType) => {
    const { cache = true, name, type, options } = filter;

    const fetch = async () => {
      const options = await fetchOptions(filter, this.ngApiClient, data);
      this.setCache([name, { isLoaded: true }]);
      options && this.mutateFilterScheme(name, { options });
      return options;
    };

    /**
     * Здесь мы пытаемся загрузить список опций если:
     * - они еще не загружены, в таком случае они будут = `null`
     * - если они загружены, но флаг `cache = false`, т.е. не нужно кешировать и каждый раз при открытия списка мы будем их загружать
     */
    const isCanLoaded = type === 'options' && (!this.getCache(name)?.isLoaded || !cache);
    if (!Array.isArray(options) || isCanLoaded) {
      return await fetch();
    }
  };

  getValueList() {
    return this.valueListSubject.getValue();
  }

  getScheme() {
    return this.schemeSubject.getValue();
  }
}
